import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Heart, Users, MessageCircle, Flag, CheckCircle } from 'lucide-react';

const CommunityGuidelines = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-warm">
      <Helmet>
        <title>Community Guidelines | UnityLove</title>
        <meta name="description" content="UnityLove community guidelines for respectful, consent-based interactions in our Black poly dating community." />
        <link rel="canonical" href="https://joinunitylove.com/guidelines" />
      </Helmet>
      {/* Header */}
      <nav className="bg-white/90 dark:bg-slate-950/80 backdrop-blur-sm border-b border-rose-100 dark:border-slate-800 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 dark:text-slate-200 hover:text-rose-600"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <Users className="w-6 h-6 text-rose-600" />
              <span className="font-playfair text-xl font-bold text-gray-900 dark:text-white">
                Community Guidelines
              </span>
            </div>

            <div className="w-20"></div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="glass rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h1 className="font-playfair text-4xl font-bold text-gray-900 mb-4">
              Community Guidelines
            </h1>
            <p className="text-xl text-gray-600">
              Building a respectful, inclusive space for meaningful connections
            </p>
            <div className="text-sm text-gray-500 mt-4">
              Last updated: January 2025
            </div>
          </div>

          <div className="space-y-8">
            {/* Core Values */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <Heart className="w-6 h-6 text-rose-600 fill-current" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  Our Core Values
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-rose-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 text-rose-600 mr-2" />
                    Consent & Communication
                  </h3>
                  <p className="text-gray-700">
                    All interactions must be based on clear, enthusiastic consent. Be transparent about your intentions, boundaries, and relationship status.
                  </p>
                </div>
                
                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 text-purple-600 mr-2" />
                    Cultural Respect
                  </h3>
                  <p className="text-gray-700">
                    We celebrate African American and African heritage. Embrace diversity while honoring shared cultural values and traditions.
                  </p>
                </div>
                
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-2" />
                    Authentic Connections
                  </h3>
                  <p className="text-gray-700">
                    Be genuine in your profile and communications. Honest representation builds trust and meaningful relationships.
                  </p>
                </div>
                
                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    Inclusive Community
                  </h3>
                  <p className="text-gray-700">
                    Welcome all relationship styles, identities, and backgrounds within our community guidelines. Discrimination is not tolerated.
                  </p>
                </div>
              </div>
            </section>

            {/* The L.O.V.E.R.S. Framework */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-6">
                The L.O.V.E.R.S. Community Framework
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-red-600">L</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Learn</h3>
                    <p className="text-gray-700">
                      This is a community for learning, friendship, and connection—not just casual encounters. Approach interactions with curiosity and respect.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-orange-600">O</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Open Communication</h3>
                    <p className="text-gray-700">
                      Practice honest, transparent communication about your needs, boundaries, and expectations. Clear communication prevents misunderstandings.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-green-600">V</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Verbal Consent</h3>
                    <p className="text-gray-700">
                      Always ask before sharing personal information, photos, or meeting in person. Respect "no" as a complete answer.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-blue-600">E</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Expectations</h3>
                    <p className="text-gray-700">
                      Leave assumptions at the door. Embrace diversity in relationship styles, cultural backgrounds, and personal journeys.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-purple-600">R</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Respectful Behavior</h3>
                    <p className="text-gray-700">
                      Treat everyone with dignity and kindness. Disagreements should be handled with empathy and maturity.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-pink-600">S</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Safer Spaces</h3>
                    <p className="text-gray-700">
                      Help maintain an inclusive, safe environment for all identities and relationship styles. Report unsafe behavior immediately.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Profile Guidelines */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-6">
                Profile Guidelines
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center text-green-700">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Do Include
                  </h3>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Recent, authentic photos of yourself</li>
                    <li>• Honest description of your situation and interests</li>
                    <li>• Clear communication about what you're seeking</li>
                    <li>• Your cultural background and values</li>
                    <li>• Boundaries and preferences</li>
                    <li>• Interests and hobbies that define you</li>
                  </ul>
                </div>
                
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center text-red-700">
                    <Flag className="w-5 h-5 mr-2" />
                    Don't Include
                  </h3>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Photos of other people without consent</li>
                    <li>• Explicit sexual content or nudity</li>
                    <li>• False information about age, status, or identity</li>
                    <li>• Contact information for other platforms</li>
                    <li>• Discriminatory language or hate speech</li>
                    <li>• Commercial advertisements or promotions</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Communication Standards */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <MessageCircle className="w-6 h-6 text-rose-600" />
                <h2 className="font-playfair text-2xl font-semibold text-gray-900">
                  Communication Standards
                </h2>
              </div>
              
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 text-green-700">
                    Respectful Messaging
                  </h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Start conversations with genuine interest</li>
                    <li>• Read profiles before messaging</li>
                    <li>• Ask questions and show curiosity about the person</li>
                    <li>• Respect response times and availability</li>
                    <li>• Accept rejection gracefully</li>
                  </ul>
                </div>
                
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 text-red-700">
                    Unacceptable Messages
                  </h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Explicit sexual content or requests</li>
                    <li>• Harassment or persistent messaging after "no"</li>
                    <li>• Discriminatory or hateful language</li>
                    <li>• Spam or copy-paste messages</li>
                    <li>• Pressuring for personal information or meetings</li>
                    <li>• Sending unsolicited intimate photos</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Safety Guidelines */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-6">
                Meeting Safety Guidelines
              </h2>
              
              <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-4">Before Meeting In Person</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="text-gray-700 space-y-2">
                    <li>• Video chat first to verify identity</li>
                    <li>• Meet in public, well-lit locations</li>
                    <li>• Tell a friend where you're going</li>
                    <li>• Arrange your own transportation</li>
                  </ul>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Trust your instincts about safety</li>
                    <li>• Don't share home address initially</li>
                    <li>• Keep first meetings short and casual</li>
                    <li>• Have an exit plan if uncomfortable</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Reporting and Enforcement */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-6">
                Reporting and Community Enforcement
              </h2>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">How to Report</h3>
                  <p className="text-gray-700 mb-3">
                    If you encounter behavior that violates our guidelines:
                  </p>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Use the in-app reporting feature on profiles or messages</li>
                    <li>• Email: safety@joinunitylove.com</li>
                    <li>• Include screenshots and context when possible</li>
                    <li>• Block users who make you uncomfortable</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Enforcement Actions</h3>
                  <ul className="text-gray-700 space-y-1">
                    <li>• <strong>Warning:</strong> First-time minor violations</li>
                    <li>• <strong>Temporary Suspension:</strong> Repeated or moderate violations</li>
                    <li>• <strong>Permanent Ban:</strong> Severe violations or repeated offenses</li>
                    <li>• <strong>Legal Action:</strong> Criminal behavior reported to authorities</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-playfair text-2xl font-semibold text-gray-900 mb-4">
                Questions About Guidelines
              </h2>
              <div className="bg-rose-50 p-4 rounded-lg">
                <p className="text-gray-700 mb-3">
                  Need clarification about our community standards?
                </p>
                <div className="space-y-2 text-gray-700">
                  <div>📧 Community Team: community@joinunitylove.com</div>
                  <div>🛡️ Safety Concerns: safety@joinunitylove.com</div>
                </div>
                <p className="text-gray-500 text-sm mt-2">
                  Email us and we'll respond within 1–2 business days.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelines;